import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useCatalog } from "../../catalog/CatalogContext";
import ShareButtons from "../../components/Buttons/ShareButtons";
import OrderModal from "../../components/OrderModal/OrderModal";
import { useCart } from "../../cart/CartContext";
import { buildProductSlug, getProductBySlug } from "../../data/products";
import { formatPrice } from "../../cart/cartUtils";

const detailBadges = ["Elegant", "Tendance", "A decouvrir"];

export default function ProductDetail() {
  const { slug } = useParams();
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const { products, loading, error } = useCatalog();
  const product = slug ? getProductBySlug(products, slug) : undefined;
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");

  const requiresSize = product?.category === "shoes";
  const isOutOfStock = (product?.stock ?? 0) <= 0;
  const totalPrice = useMemo(() => {
    if (!product) return 0;
    return product.priceValue * quantity;
  }, [product, quantity]);

  useEffect(() => {
    setQuantity(1);
    setSelectedSize("");
  }, [product?.id]);

  if (loading) {
    return (
      <section className="px-4 pb-20 pt-40 md:px-8 lg:px-10">
        <div className="surface-panel mx-auto max-w-3xl rounded-[36px] px-6 py-20 text-center">
          <p className="brand-display text-4xl font-bold text-[#21140f]">
            Chargement du produit...
          </p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="px-4 pb-20 pt-40 md:px-8 lg:px-10">
        <div className="surface-panel mx-auto max-w-3xl rounded-[36px] px-6 py-20 text-center">
          <p className="brand-display text-4xl font-bold text-[#21140f]">
            Catalogue indisponible
          </p>
          <p className="mx-auto mt-4 max-w-md text-sm leading-7 text-[#7a6357]">
            {error}
          </p>
        </div>
      </section>
    );
  }

  if (!product) {
    return (
      <section className="px-4 pb-20 pt-40 md:px-8 lg:px-10">
        <div className="surface-panel mx-auto max-w-3xl rounded-[36px] px-6 py-20 text-center">
          <p className="brand-display text-4xl font-bold text-[#21140f]">
            Produit introuvable
          </p>
          <p className="mx-auto mt-4 max-w-md text-sm leading-7 text-[#7a6357]">
            Ce produit n’est plus disponible pour le moment.
          </p>
          <Link
            to="/products"
            className="cta-primary mt-8 inline-flex rounded-full px-6 py-4 text-xs font-semibold uppercase tracking-[0.3em]"
          >
            Retour boutique
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="px-4 pb-20 pt-36 md:px-8 lg:px-10 lg:pt-40">
        <div className="mx-auto max-w-7xl">
          <div className="surface-panel rounded-[36px] p-5 md:p-8 lg:p-10">
            <div className="mb-8 flex flex-wrap items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8d6e5d]">
              <Link to="/" className="hover:text-[#6e2d16]">
                Accueil
              </Link>
              <span>/</span>
              <Link to="/products" className="hover:text-[#6e2d16]">
                Boutique
              </Link>
              <span>/</span>
              <span>{product.title}</span>
            </div>

            <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="surface-card rounded-[34px] p-5 md:p-8">
                <div className="rounded-[28px] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.94),rgba(240,223,208,0.88))] p-6 md:p-10">
                  <div className="flex items-center justify-between gap-4">
                    <span className="rounded-full bg-white/80 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#8b634d]">
                      {product.category === "bags" ? "Sac" : "Chaussure"}
                    </span>
                    {product.isNew && (
                      <span className="rounded-full bg-[#251713] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#fff0e3]">
                        Nouveaute
                      </span>
                    )}
                  </div>

                  <div className="flex min-h-[420px] items-center justify-center py-8">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="max-h-[380px] max-w-full object-contain"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-6">
                <div className="rounded-[34px] bg-[#241712] px-6 py-8 text-[#fff4ea] md:px-8 md:py-10">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#e3c6b3]">
                    A decouvrir
                  </p>
                  <h1 className="brand-display mt-4 text-4xl font-bold leading-tight md:text-6xl">
                    {product.title}
                  </h1>

                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    <span className="brand-display text-3xl font-bold md:text-4xl">
                      {product.price}
                    </span>
                    <span className="rounded-full bg-white/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#f2d8c8]">
                      {isOutOfStock ? "Rupture" : `Stock ${product.stock ?? 0}`}
                    </span>
                  </div>

                  <p className="mt-6 text-base leading-8 text-[#f2dfd3]">
                    {product.description ??
                      "Un joli modele pense pour apporter du style et de l’elegance a votre tenue."}
                  </p>
                </div>

                <div className="surface-card rounded-[34px] p-6 md:p-8">
                  <div className="grid gap-6">
                    {requiresSize && product.sizes && (
                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8b6d5d]">
                          Pointure
                        </label>
                        <select
                          className="mt-3 w-full rounded-[20px] border border-[rgba(62,35,24,0.12)] bg-white/75 px-4 py-4 text-sm text-[#221510] outline-none"
                          value={selectedSize}
                          onChange={(event) => setSelectedSize(event.target.value)}
                        >
                          <option value="">Choisir une pointure</option>
                          {product.sizes.map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8b6d5d]">
                        Quantite
                      </label>
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <div className="inline-flex items-center rounded-full border border-[rgba(62,35,24,0.12)] bg-white/75 p-1">
                          <button
                            type="button"
                            onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                            className="flex h-11 w-11 items-center justify-center rounded-full text-lg text-[#2b1b15]"
                          >
                            −
                          </button>
                          <span className="min-w-[3rem] text-center text-sm font-semibold text-[#21140f]">
                            {quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => setQuantity((prev) => prev + 1)}
                            className="flex h-11 w-11 items-center justify-center rounded-full text-lg text-[#2b1b15]"
                          >
                            +
                          </button>
                        </div>
                        <div className="rounded-full bg-white/80 px-4 py-3 text-sm font-semibold text-[#3c271c]">
                          Total {formatPrice(totalPrice)}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => setIsOrderOpen(true)}
                        className="cta-primary rounded-full px-6 py-4 text-xs font-semibold uppercase tracking-[0.3em] transition"
                      >
                        Commander maintenant
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          if (requiresSize && !selectedSize) return;
                          addItem({
                            productId: product.id,
                            quantity,
                            size: selectedSize || undefined,
                          });
                        }}
                        disabled={isOutOfStock || (requiresSize && !selectedSize)}
                        className="cta-secondary rounded-full px-6 py-4 text-xs font-semibold uppercase tracking-[0.3em] transition disabled:opacity-45"
                      >
                        Ajouter au panier
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      {detailBadges.map((badge) => (
                        <span
                          key={badge}
                          className="rounded-full border border-[rgba(62,35,24,0.1)] bg-white/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#745d51]"
                        >
                          {badge}
                        </span>
                      ))}
                    </div>

                    <div className="rounded-[26px] border border-[rgba(62,35,24,0.08)] bg-white/70 p-5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8b6d5d]">
                        Partager ce produit
                      </p>
                      <div className="mt-4">
                        <ShareButtons
                          title={product.title}
                          price={product.price}
                          slug={buildProductSlug(product)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <OrderModal
        product={isOrderOpen ? product : null}
        onClose={() => setIsOrderOpen(false)}
      />
    </>
  );
}
